/**
 * hooks/useSettingsProfile.ts
 *
 * Encapsule la logique de données pour la section Paramètres > Profil.
 * Charge le profil utilisateur depuis la table `users` et expose les méthodes save/reset.
 * Suit les modifications non enregistrées via le flag `hasChanges`.
 * Updated: ajout de la gestion du logo de profil (logoFile, logoPreviewUrl).
 *   - Upload du nouveau logo dans le bucket `form-logos`
 *   - Mise à jour de users.logo_url
 *   - Appel de refreshProfile pour synchroniser le contexte global après sauvegarde
 * Updated: ajout de stamp_title et stamp_toque_number pour la configuration du tampon
 *   circulaire auto-généré côté client.
 * Updated: catch block now logs the thrown error via console.error before returning the
 *   user-facing error message, to aid debugging of silent save failures.
 * Updated: replaced hardcoded 'form-logos' strings with shared constants from utils/constants.
 * Updated: refreshProfile() is now called outside the main try/catch so a context-refresh failure
 *   no longer masks a successful DB save (it is wrapped in its own try/catch that only logs).
 * Updated: file extension extraction is now robust -- validates the candidate ext, falls back to
 *   MIME type or 'bin', and normalizes to lowercase before constructing the storage path.
 * Updated: ajout des préférences de notification (notification_immediate, notification_daily_digest)
 *   gérées via un état séparé notificationPrefs / initialNotificationPrefs; sauvegardées en même
 *   temps que le profil via saveProfile().
 * Updated Apr 2026 (single logo source of truth): cascade update to form_templates removed.
 *   Formulaires now resolve their logo live from users.logo_url at render time, so no
 *   cascade is needed when the user updates their profile logo.
 * Updated Apr 2026 (onboarding auto-dismiss): saveProfile now sets onboarding_dismissed=true
 *   in the same UPDATE when both logo_url and stamp_title are non-null after saving, so the
 *   Dashboard onboarding card disappears automatically once both items are completed.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { FORM_LOGOS_BUCKET } from '../utils/constants';
import { captureError } from '../utils/errorReporting';

export interface ProfileData {
  email: string;
  first_name: string;
  last_name: string;
  stamp_title: string;
  stamp_toque_number: string;
  created_at: string;
}

export interface NotificationPrefs {
  notification_immediate: boolean;
  notification_daily_digest: boolean;
}

const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  notification_immediate: true,
  notification_daily_digest: false,
};

const EDITABLE_FIELDS: (keyof Omit<ProfileData, 'email' | 'created_at'>)[] = [
  'first_name',
  'last_name',
  'stamp_title',
  'stamp_toque_number',
];

function toFormValue(val: string | null | undefined): string {
  return val ?? '';
}

function toDbValue(val: string): string | null {
  const trimmed = val.trim();
  return trimmed === '' ? null : trimmed;
}

function hasUnsavedProfileChanges(current: ProfileData, initial: ProfileData): boolean {
  return EDITABLE_FIELDS.some((f) => current[f].trim() !== initial[f].trim());
}

function hasUnsavedNotificationChanges(current: NotificationPrefs, initial: NotificationPrefs): boolean {
  return current.notification_immediate !== initial.notification_immediate
    || current.notification_daily_digest !== initial.notification_daily_digest;
}

function resolveFileExtension(file: File): string {
  const dotIdx = file.name.lastIndexOf('.');
  if (dotIdx !== -1) {
    const candidate = file.name.slice(dotIdx + 1);
    if (/^[a-zA-Z0-9]+$/.test(candidate)) {
      return candidate.toLowerCase();
    }
  }
  const mimePart = file.type.split('/')[1];
  if (mimePart && /^[a-zA-Z0-9]+$/.test(mimePart)) {
    return mimePart.toLowerCase();
  }
  return 'bin';
}

export function useSettingsProfile() {
  const { user, refreshProfile } = useAuth();

  const [initialProfile, setInitialProfile] = useState<ProfileData | null>(null);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [initialLogoUrl, setInitialLogoUrl] = useState<string | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPrefs>(DEFAULT_NOTIFICATION_PREFS);
  const [initialNotificationPrefs, setInitialNotificationPrefs] = useState<NotificationPrefs>(DEFAULT_NOTIFICATION_PREFS);

  const loadProfile = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    const { data, error } = await supabase
      .from('users')
      .select('email, first_name, last_name, created_at, logo_url, stamp_title, stamp_toque_number, notification_immediate, notification_daily_digest')
      .eq('id', user.id)
      .maybeSingle();

    if (!error && data) {
      const loaded: ProfileData = {
        email: data.email,
        first_name: toFormValue(data.first_name),
        last_name: toFormValue(data.last_name),
        stamp_title: toFormValue(data.stamp_title),
        stamp_toque_number: toFormValue(data.stamp_toque_number),
        created_at: data.created_at,
      };
      setInitialProfile(loaded);
      setProfile(loaded);
      setInitialLogoUrl(data.logo_url ?? null);
      setLogoPreviewUrl(data.logo_url ?? null);
      setLogoFile(null);

      const loadedPrefs: NotificationPrefs = {
        notification_immediate: data.notification_immediate ?? true,
        notification_daily_digest: data.notification_daily_digest ?? false,
      };
      setNotificationPrefs(loadedPrefs);
      setInitialNotificationPrefs(loadedPrefs);
    }

    setLoading(false);
  }, [user]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const updateField = useCallback((field: keyof Omit<ProfileData, 'email' | 'created_at'>, value: string) => {
    setProfile((prev) => prev ? { ...prev, [field]: value } : prev);
  }, []);

  const updateNotificationPref = useCallback((key: keyof NotificationPrefs, value: boolean) => {
    setNotificationPrefs((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleLogoChange = useCallback((file: File | null, url: string | null) => {
    setLogoFile(file);
    setLogoPreviewUrl(url);
  }, []);

  const saveProfile = useCallback(async (): Promise<{ error: string | null }> => {
    if (!user || !profile) return { error: 'Profil introuvable' };
    setSaving(true);

    let saveResult: { error: string | null };

    try {
      let newLogoUrl: string | null = logoPreviewUrl;

      if (logoFile) {
        const fileExt = resolveFileExtension(logoFile);
        const fileName = `${user.id}/profile-logo-${crypto.randomUUID()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from(FORM_LOGOS_BUCKET)
          .upload(fileName, logoFile);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from(FORM_LOGOS_BUCKET)
          .getPublicUrl(fileName);

        newLogoUrl = publicUrl;
      }

      const stampTitleValue = toDbValue(profile.stamp_title);
      const shouldAutoDismiss = !!newLogoUrl && !!stampTitleValue;

      const updatePayload: Record<string, unknown> = {
        first_name: toDbValue(profile.first_name),
        last_name: toDbValue(profile.last_name),
        logo_url: newLogoUrl,
        stamp_title: stampTitleValue,
        stamp_toque_number: toDbValue(profile.stamp_toque_number),
        notification_immediate: notificationPrefs.notification_immediate,
        notification_daily_digest: notificationPrefs.notification_daily_digest,
      };

      if (shouldAutoDismiss) {
        updatePayload.onboarding_dismissed = true;
      }

      const { error } = await supabase
        .from('users')
        .update(updatePayload)
        .eq('id', user.id);

      if (error) throw error;

      setInitialProfile({ ...profile });
      setInitialLogoUrl(newLogoUrl);
      setLogoPreviewUrl(newLogoUrl);
      setLogoFile(null);
      setInitialNotificationPrefs({ ...notificationPrefs });

      saveResult = { error: null };
    } catch (err) {
      console.error('Profile save failed', err);
      captureError(err, { hook: 'useSettingsProfile', action: 'saveProfile', userId: user.id });
      saveResult = { error: 'Une erreur est survenue lors de la sauvegarde.' };
    } finally {
      setSaving(false);
    }

    if (!saveResult.error) {
      try {
        await refreshProfile();
      } catch (refreshErr) {
        console.error('refreshProfile failed after successful save', refreshErr);
        captureError(refreshErr, { hook: 'useSettingsProfile', action: 'refreshProfileAfterSave', userId: user?.id });
      }
    }

    return saveResult;
  }, [user, profile, logoFile, logoPreviewUrl, notificationPrefs, refreshProfile]);

  const resetChanges = useCallback(() => {
    if (initialProfile) setProfile({ ...initialProfile });
    setLogoPreviewUrl(initialLogoUrl);
    setLogoFile(null);
    setNotificationPrefs({ ...initialNotificationPrefs });
  }, [initialProfile, initialLogoUrl, initialNotificationPrefs]);

  const hasLogoChanged = logoFile !== null || logoPreviewUrl !== initialLogoUrl;
  const hasProfileChanged = profile && initialProfile ? hasUnsavedProfileChanges(profile, initialProfile) : false;
  const hasNotificationChanged = hasUnsavedNotificationChanges(notificationPrefs, initialNotificationPrefs);
  const hasChanges = hasProfileChanged || hasLogoChanged || hasNotificationChanged;

  return {
    profile,
    loading,
    saving,
    hasChanges,
    logoFile,
    logoPreviewUrl,
    notificationPrefs,
    updateField,
    updateNotificationPref,
    handleLogoChange,
    saveProfile,
    resetChanges,
  };
}
